import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useExpenseCategoryReport,
  useExportExpenseCategoryReport,
} from '../../../services/httpServices/queries';
import { ExpensesLayout } from './ExpensesLayout';
import { DatePicker } from '../../../components/ui/DatePicker';
import {
  MONTH_NAMES,
  monthBounds,
  yearBounds,
  quickRanges,
  todayInTimeZone,
} from '../../../utils/reportPeriod';
import { useHospitalTimezone } from '../../../hooks/useHospitalTimezone';
import { formatDateLabel, formatOrdinalDateTimeInTimeZone } from '../../../utils/dateFormat';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Calendar,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  Layers,
  Loader2,
  Table2,
} from 'lucide-react';
import type { ReportColumn, ReportResult, ReportValueFormat } from '../../../types/reports';

const CURRENCY = '৳';

/** The report types the page can show (the "Report Type" dropdown). */
const GROUP_BY_OPTIONS = [
  { value: 'category', label: 'By Categories', dimension: 'Category' },
  { value: 'category-no-date', label: 'By Categories (No Date)', dimension: 'Category' },
  { value: 'vendor', label: 'By Vendors', dimension: 'Vendor' },
  { value: 'paymentMethod', label: 'By Payment Method', dimension: 'Payment Method' },
];

function formatCurrency(value: unknown): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatCell(value: unknown, format?: ReportValueFormat, timezone?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (format) {
    case 'currency': {
      const amount = Number(value);
      return Number.isFinite(amount) ? formatCurrency(amount) : String(value);
    }
    case 'number': {
      const amount = Number(value);
      return Number.isFinite(amount) ? amount.toLocaleString('en-US') : String(value);
    }
    case 'date':
      return formatDateLabel(String(value));
    case 'datetime':
      return formatOrdinalDateTimeInTimeZone(String(value), timezone);
    case 'percent': {
      const amount = Number(value);
      return Number.isFinite(amount) ? `${amount.toLocaleString('en-US')}%` : String(value);
    }
    default:
      return String(value);
  }
}

function compareValues(a: unknown, b: unknown, format?: ReportValueFormat): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (format === 'date' || format === 'datetime') {
    const aTime = new Date(String(a)).getTime();
    const bTime = new Date(String(b)).getTime();
    if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
  }
  if (format === 'number' || format === 'currency' || format === 'percent') {
    const aNum = Number(a);
    const bNum = Number(b);
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Derives the Month/Year dropdown selection from a From/To pair. */
function derivePeriod(from: string, to: string): { month: number | 'all' | 'custom'; year: number } {
  const year = Number(from.slice(0, 4)) || new Date().getFullYear();
  if (!from || !to) return { month: 'custom', year };
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  if (fy === ty && fm === tm && fd === 1 && td === new Date(fy, fm, 0).getDate()) {
    return { month: fm - 1, year: fy };
  }
  if (fy === ty && fm === 1 && fd === 1 && tm === 12 && td === 31) {
    return { month: 'all', year: fy };
  }
  return { month: 'custom', year };
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-xl font-bold text-text break-words">{value}</p>
    </div>
  );
}

export function ExpenseCategoryReport() {
  const [searchParams, setSearchParams] = useSearchParams();

  // The report covers the hospital's calendar (Settings → General), not the
  // workstation's; wait for the zone before seeding "this month".
  const { timezone: hospitalTimezone, isReady: timezoneReady } = useHospitalTimezone();
  const today = useMemo(() => todayInTimeZone(hospitalTimezone), [hospitalTimezone]);
  const defaultRange = useMemo(
    () => monthBounds(today.getFullYear(), today.getMonth()),
    [today],
  );

  // Seed the URL so the view (type + period) survives a reload.
  const seededDefaults = useRef(false);
  useEffect(() => {
    if (seededDefaults.current || !timezoneReady) return;
    seededDefaults.current = true;

    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!next.get('type')) {
      next.set('type', 'category');
      changed = true;
    }
    if (!next.get('from')) {
      next.set('from', defaultRange.from);
      changed = true;
    }
    if (!next.get('to')) {
      next.set('to', defaultRange.to);
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
    // `searchParams`/`setSearchParams` are intentionally omitted: the effect
    // must run once after the timezone arrives, not on every URL edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneReady, defaultRange.from, defaultRange.to]);

  const groupBy = searchParams.get('type') || 'category';
  const dateFrom = searchParams.get('from') || defaultRange.from;
  const dateTo = searchParams.get('to') || defaultRange.to;

  const setParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const filters = useMemo(() => ({ groupBy, dateFrom, dateTo }), [groupBy, dateFrom, dateTo]);
  const { data, isFetching, error } = useExpenseCategoryReport(filters);
  const result: ReportResult | undefined = data?.data;
  const exportReport = useExportExpenseCategoryReport();

  const period = derivePeriod(dateFrom, dateTo);

  const ranges = useMemo(() => quickRanges(today), [today]);
  const applyRange = (from: string, to: string) => setParams({ from, to });
  const yearOptions = useMemo(() => {
    const current = today.getFullYear();
    const years: number[] = [];
    for (let year = current; year >= current - 6; year -= 1) years.push(year);
    if (period.year < current - 6) years.push(period.year);
    return years;
  }, [today, period.year]);

  const applyMonth = (month: number | 'all') => {
    const range = month === 'all' ? yearBounds(period.year) : monthBounds(period.year, month);
    setParams({ from: range.from, to: range.to });
  };
  const applyYear = (year: number) => {
    const range =
      period.month === 'all' || period.month === 'custom'
        ? yearBounds(year)
        : monthBounds(year, period.month);
    setParams({ from: range.from, to: range.to });
  };

  const dimensionLabel = GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.dimension ?? 'Category';
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const columns = result?.columns ?? [];
  const sortedRows = useMemo(() => {
    const rows = result?.rows ?? [];
    if (!sortKey) return rows;
    const column = columns.find((item) => item.key === sortKey);
    return [...rows].sort((a, b) => {
      const cmp = compareValues(a[sortKey], b[sortKey], column?.format);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, sortKey, sortDir]);

  const toggleSort = (column: ReportColumn) => {
    if (sortKey === column.key) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(column.key);
      setSortDir('asc');
    }
  };

  const handleExport = (format: 'pdf' | 'csv' | 'excel') => {
    exportReport.mutate({ format, filters });
  };

  const summaryValue = (value: number | string, format?: ReportValueFormat) => {
    if (format === 'currency') return formatCurrency(value);
    if (format === 'number') return (Number(value) || 0).toLocaleString('en-US');
    return String(value);
  };

  return (
    <ExpensesLayout
      title="Expenses by Category"
      description="Category-wise expense totals for the selected day, month or year"
      helpKey="/billing/expenses"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={!result || exportReport.isPending}
            className="px-3 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-40"
          >
            {exportReport.isPending && exportReport.variables?.format === 'pdf' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Download size={15} />
            )}
            PDF
          </button>
          <button
            type="button"
            onClick={() => handleExport('excel')}
            disabled={!result || exportReport.isPending}
            className="px-3 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-40"
          >
            {exportReport.isPending && exportReport.variables?.format === 'excel' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <FileSpreadsheet size={15} />
            )}
            Excel
          </button>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={!result || exportReport.isPending}
            className="px-3 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-40"
          >
            {exportReport.isPending && exportReport.variables?.format === 'csv' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Table2 size={15} />
            )}
            CSV
          </button>
        </div>
      }
    >
      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text mb-2">
              <Layers size={14} />
              Report Type
            </label>
            <select
              value={groupBy}
              onChange={(event) => setParams({ type: event.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {GROUP_BY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text mb-2">
              <Calendar size={14} />
              Month
            </label>
            <select
              value={period.month === 'custom' ? 'custom' : String(period.month)}
              onChange={(event) => {
                const value = event.target.value;
                applyMonth(value === 'all' ? 'all' : Number(value));
              }}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {period.month === 'custom' && (
                <option value="custom" disabled>
                  Custom range
                </option>
              )}
              <option value="all">Full Year</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text mb-2">
              <Calendar size={14} />
              Year
            </label>
            <select
              value={period.year}
              onChange={(event) => applyYear(Number(event.target.value))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-text mb-2">
                <Calendar size={14} />
                Date From
              </label>
              <DatePicker
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(value) => setParams({ from: value })}
                timeZone={hospitalTimezone}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-text mb-2">
                <Calendar size={14} />
                Date To
              </label>
              <DatePicker
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(value) => setParams({ to: value })}
                timeZone={hospitalTimezone}
              />
            </div>
          </div>

          <div className="md:col-span-2 xl:col-span-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-text-muted">Quick range:</span>
              {ranges.map((range) => {
                const active = dateFrom === range.from && dateTo === range.to;
                return (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => applyRange(range.from, range.to)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-primary text-white border-primary'
                        : 'bg-background text-text-muted border-border hover:text-text'
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to generate the report.</span>
        </div>
      )}

      {isFetching && !result ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : result ? (
        <div className="space-y-6">
          <p className="text-sm text-text-muted">
            Generated {formatOrdinalDateTimeInTimeZone(result.generatedAt, result.timezone)} ·{' '}
            <span className="text-text font-medium">
              {result.total.toLocaleString('en-US')} {result.total === 1 ? 'entry' : 'entries'}
            </span>{' '}
            by {dimensionLabel.toLowerCase()}
          </p>

          {result.summary.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {result.summary.map((item) => (
                <SummaryCard
                  key={item.label}
                  label={item.label}
                  value={summaryValue(item.value, item.format)}
                />
              ))}
            </div>
          )}

          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-text">Expenses by {dimensionLabel}</h3>
              <span className="text-xs text-text-muted">
                {result.total} {result.total === 1 ? dimensionLabel.toLowerCase() : `${dimensionLabel.toLowerCase()}s`}
              </span>
            </div>
            {result.rows.length === 0 ? (
              <div className="px-4 py-12 text-center text-text-muted">
                <Table2 size={32} className="mx-auto mb-3 opacity-40" />
                No expenses found for the selected period.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      {columns.map((column) => {
                        const isSorted = sortKey === column.key;
                        return (
                          <th
                            key={column.key}
                            className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase whitespace-nowrap"
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort(column)}
                              className={`inline-flex items-center gap-1 hover:text-text transition-colors ${
                                isSorted ? 'text-text' : ''
                              }`}
                            >
                              {column.label}
                              {isSorted ? (
                                sortDir === 'asc' ? (
                                  <ArrowUp size={13} />
                                ) : (
                                  <ArrowDown size={13} />
                                )
                              ) : (
                                <ChevronsUpDown size={13} className="opacity-40" />
                              )}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedRows.map((row, index) => (
                      <tr key={index} className="hover:bg-background/50 transition-colors">
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={`px-4 py-3 text-sm whitespace-nowrap ${
                              column.format === 'currency' || column.format === 'number'
                                ? 'text-right font-medium text-text tabular-nums'
                                : 'text-text'
                            }`}
                          >
                            {formatCell(row[column.key], column.format, result.timezone)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </ExpensesLayout>
  );
}
